import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';


import useOutsideClick from '../../hooks/useOutsideClick';
import './index.scss'; // Assuming you have a CSS file for styles
import { config } from '../../config';
import ImagePlaceholder from '../Global/ImagePlaceholder';
import { openModal } from '../../redux/Slices/modalSlice';
import SearchResults from './SearchResults';
import Logo, { LogoLight, LogoMobile, LogoMobileLight } from './Logo';
import BrandLogo from '../WhiteLabel/BrandLogo';
import { useWhiteLabel } from '../../context/WhiteLabelContext';
import { useCanCreateWorkspace } from '../../hooks/useCanCreateWorkspace';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import useHandleExpandableColumnClick from '../../hooks/useHandleExpandableColumnClick';
import useDebouncedClick from '../../hooks/useDebouncedClick';
import useNavigationGuard from '../../hooks/useNavigationGuard';

import { setSelectedBackground } from '../../redux/Slices/boardSlice';
import { useTheme } from '../../context/ThemeContext';
import MemberOnlineStatus from '../Global/MemberOnlineStatus';

import Search from '../icons/Search';
import Campaign from '../icons/Campaign';
import TextSnippet from '../icons/TextSnippet';

import { useSocketEvent } from '../../hooks/useSocketManager';
import NewLabel from '../icons/NewLabel';
import NotificationsIcon from '../icons/Notifications';
import Home from '../icons/Home';
import Add from '../icons/Add';
import MyDay from '../icons/MyDay';
import { closeExpandableColumn } from '../../redux/Slices/expandableColumnSlice';
import { fetchTodayTasks } from '../../redux/Slices/todayTaskSlice';
import { fetchUnreadCount } from '../../redux/Slices/chatSlice';
import Messages from '../icons/Messages';
import Workspaces from '../icons/Workspaces';
import Close from '../icons/Close';
import SidebarBoardList from './SidebarBoardList';
import { fetchWorkspaces, fetchGuestsWorkspaces } from '../../redux/Slices/workspaceSlice';

// import useOnlineStatus from '../../hooks/useOnlineStatus';


const Sidebar = () => {
  const [isShowProfile, setIsShowProfile] = useState(false);
  const [newNotification, setNewNotification] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [isShowSearchResult, setSearchResult] = useState(false);
  const [query, setQuery] = useState(""); // Search query
  const [activeNav, setActiveNav] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReleaseTip, setShowReleaseTip] = useState(false);
  const [hasNewRelease, setHasNewRelease] = useState(false);
  const chatUnreadCount = useSelector((state) => state.chat?.unreadCount || 0);

  // White label context
  const { isWhiteLabel } = useWhiteLabel();

  const location = useLocation();
  const profileRef = useRef(null);
  const notificationsRef = useRef(null);
  const searchRef = useRef(null);

  const workspaceRef = useRef(null);
  const recentRef = useRef(null);
  const starredRef = useRef(null);
  const navigate = useNavigate();

  const { board } = useSelector((state) => state.board);
  const { workspaces, guestWorkspaces } = useSelector((state) => state.workspaces);
  const myDayTasks = useSelector((state) => state.todayTask?.tasks || []);
  const myDayIncompleteCount = myDayTasks.filter(t => !t.completed).length;
  const { theme } = useTheme();

  // Detect board/card route (independent of board data loading)
  const isBoardRoute = location.pathname.startsWith('/b/') || location.pathname.startsWith('/c/');
  // Full board context: route + board data loaded
  const isBoardContext = isBoardRoute && board?.wShortId;

  // Find current workspace's boards (search both member and guest workspaces)
  const currentWorkspace = isBoardContext
    ? workspaces.find(ws => ws.shortId === board.wShortId)
      || guestWorkspaces.find(ws => ws.shortId === board.wShortId)
    : null;
  const workspaceBoards = currentWorkspace?.boards || [];

  const { handlePopoverClick } = useHandlePopoverClick();
  const { handleExpandableColumnClick, expandableColumnState } = useHandleExpandableColumnClick();
  const { guardedNavigate, canNavigate } = useNavigationGuard();

  const dispatch = useDispatch();
  const { user, error: userError } = useSelector((state) => state.user);

  // On a white-label domain, only the WL owner may create a workspace.
  const canCreateWorkspace = useCanCreateWorkspace();
  const sidebarItems = user?.user?.settings?.sidebarItems || {};

  const [systemTheme, setSystemTheme] = useState('light'); // Default to light


  useEffect(() => {
    // Check the system theme and set it
    const updateSystemTheme = () => {
      const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setSystemTheme(isDarkMode ? 'dark' : 'light');
    };

    // Initial check
    updateSystemTheme();

    // Add a listener for changes in the system theme
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', updateSystemTheme);

    // Cleanup the event listener on unmount
    return () => {
      mediaQuery.removeEventListener('change', updateSystemTheme);
    };
  }, []);

  // Determine which logo to display
  const currentTheme = theme === 'system' ? systemTheme : theme;


  useEffect(() => {
    if (!user) return;

    const fetchNotificationCount = async () => {
      const count = await getNewNotificationsCount();
      if (count !== undefined) {
        setNewNotification(count);
        window.dispatchEvent(new CustomEvent('notificationCountUpdated', { detail: { count } }));
      }
    };

    fetchNotificationCount();
  }, [user]);

  // Bump the notification badge on live notifications via the shared connection.
  useSocketEvent('newNotification', () => {
    setNewNotification(prevCount => {
      const newCount = prevCount + 1;
      window.dispatchEvent(new CustomEvent('notificationCountUpdated', { detail: { count: newCount } }));
      return newCount;
    });
  }, []);

  // Chat unread count: fetch when in a workspace/board context, and keep it
  // live via the shared SocketManager connection (consolidated — no raw socket.on).
  useEffect(() => {
    if (board?.wShortId) {
      dispatch(fetchUnreadCount({ workspaceId: board.wShortId }));
    }
  }, [board?.wShortId, dispatch]);

  useSocketEvent('chat:newMessage', () => {
    dispatch({ type: 'chat/incrementUnreadCount' });
  }, [dispatch]);

  // Seen receipts arrive globally (ChatColumn may not be mounted).
  const currentUserId = user?.user?._id;
  useSocketEvent('chat:seen', (data) => {
    if (data?.userId !== currentUserId && data?.readAt && data?.conversationId) {
      dispatch({ type: 'chat/updateOtherLastRead', payload: { conversationId: data.conversationId, readAt: data.readAt } });
    }
  }, [currentUserId, dispatch]);


  // Ensure workspaces are loaded when in board context
  useEffect(() => {
    if (isBoardRoute && board?.wShortId && !currentWorkspace) {
      dispatch(fetchWorkspaces({ useCache: true }));
      dispatch(fetchGuestsWorkspaces());
    }
  }, [isBoardRoute, board?.wShortId, currentWorkspace, dispatch]);

  // Fetch My Day tasks when on a board route so the badge count shows
  useEffect(() => {
    if (isBoardRoute && board?.shortId) {
      const today = new Date().toISOString().split('T')[0];
      dispatch(fetchTodayTasks({ boardId: board.shortId, date: today }));
    }
  }, [isBoardRoute, board?.shortId, dispatch]);

  useEffect(() => {

    if (!location.pathname.startsWith('/b/')) {
      dispatch(setSelectedBackground(''));
    }
  }, [location.pathname]);

  // Check for new release to show badge + one-time tooltip
  const latestVersionRef = useRef(null);
  useEffect(() => {
    if (!user) return;
    const checkNewRelease = async () => {
      try {
        const res = await fetch(`${config.API_URI}/v1/releases/latest`, {
          method: 'GET',
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (data.version) {
            latestVersionRef.current = data.version;
            const lastSeen = localStorage.getItem('zoobbe_last_seen_release');
            // First-time user: no stored version yet — silently store current version
            if (!lastSeen) {
              localStorage.setItem('zoobbe_last_seen_release', data.version);
              localStorage.setItem('zoobbe_release_tip_dismissed', data.version);
              return;
            }
            if (lastSeen !== data.version) {
              setHasNewRelease(true);
              // Show tooltip only if user hasn't dismissed it for this version
              const tipDismissed = localStorage.getItem('zoobbe_release_tip_dismissed');
              if (tipDismissed !== data.version) {
                setShowReleaseTip(true);
              }
            }
          }
        }
      } catch (err) {
        // silently fail
      }
    };
    checkNewRelease();
  }, [user]);

  const dismissReleaseTip = () => {
    setShowReleaseTip(false);
    if (latestVersionRef.current) {
      localStorage.setItem('zoobbe_release_tip_dismissed', latestVersionRef.current);
    }
  };

  const handleShowProfile = () => setIsShowProfile(prev => !prev);


  const handleShowNotifications = (nav, props = {}) => {
    // Reset notification count when opening notifications
    setNewNotification(0);
    window.dispatchEvent(new CustomEvent('notificationCountUpdated', { detail: { count: 0 } }));
    addNewNotificationsCount(0);

    setActiveNav(nav);
    handleExpandableColumnClick(nav, props);

  };



  const handleCreateWorkspaceCore = () => {
    dispatch(openModal({ modalType: 'CREATE_WORKSPACE', modalData: {} }));
  };

  const handleCreateWorkspace = useDebouncedClick(handleCreateWorkspaceCore, 300);

  const handleNavClick = (nav, props = {}) => {
    // Set active state immediately for better UX
    setActiveNav(nav);
    handleExpandableColumnClick(nav, props); // Use expandable column instead of popover
  };

  // Sync activeNav with expandable column state
  useEffect(() => {
    if (expandableColumnState.isVisible) {
      setActiveNav(expandableColumnState.contentId);
    } else {
      setActiveNav(null);
    }
  }, [expandableColumnState.isVisible, expandableColumnState.contentId]);

  // Create debounced navigation handlers
  const handleWhiteLabelClickCore = () => {
    const targetPath = `/u/${user?.user?.username}/white-label`;

    if (canNavigate(targetPath)) {
      dispatch(closeExpandableColumn());
      setActiveNav(null);
      guardedNavigate(targetPath);
    }
  };

  const handleHomeClickCore = () => {
    const targetPath = `/u/${user?.user?.username}/boards`;
    const isAlreadyHome = location.pathname === targetPath;

    if (isAlreadyHome) {
      // Toggle if already home
      // handleNavClick('navWorkspaces');
    } else {
      // Go home AND open if not home
      if (canNavigate(targetPath)) {
        // Open if not already visible with same content

        guardedNavigate(targetPath);
      }
    }
  };

  const handleWhiteLabelClick = useDebouncedClick(handleWhiteLabelClickCore, 300);
  const handleHomeClick = handleHomeClickCore;


  useOutsideClick(profileRef, () => setIsShowProfile(false));
  useOutsideClick(searchRef, () => {
    setSearchResult(false);
    setIsFocused(false);
  });
  useOutsideClick(workspaceRef, () => setActiveNav(null));
  useOutsideClick(recentRef, () => setActiveNav(null));
  useOutsideClick(starredRef, () => setActiveNav(null));

  const addNewNotificationsCount = async (count) => {
    try {
      const response = await fetch(`${config.API_URI}/v1/notifications/count`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ newNotificationsCount: count }),
      });

      if (!response.ok) throw new Error('Failed to add new notification count');
      await response.json();
    } catch (error) {
      console.error('Error adding notification count:', error);
    }
  };

  const getNewNotificationsCount = async () => {
    try {
      const response = await fetch(`${config.API_URI}/v1/notifications/count`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch notification count');
      const data = await response.json();
      return data.newNotificationsCount;
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  const handleSearchClickCore = () => {
    setSearchResult((prev) => !prev);
  };

  const handleSearchClick = useDebouncedClick(handleSearchClickCore, 200);

  useEffect(() => {
    if (isShowSearchResult) {
      setIsFocused(true);
      searchRef.current.querySelector('input').select();
    }
    else {
      setIsFocused(false);
    }
  }, [isShowSearchResult]);


  const handleToggleSidebar = () => {
    setIsExpanded(prev => {
      const next = !prev;
      if (!next) {
        dispatch(closeExpandableColumn());
      }
      return next;
    });
  };

  useEffect(() => {
    const handleToggleSearch = () => {
      setSearchResult(prev => !prev);
    };
    const handleOpenCreateWorkspace = () => {
      handleCreateWorkspace();
    };

    window.addEventListener('toggleSearch', handleToggleSearch);
    window.addEventListener('openCreateWorkspace', handleOpenCreateWorkspace);

    return () => {
      window.removeEventListener('toggleSearch', handleToggleSearch);
      window.removeEventListener('openCreateWorkspace', handleOpenCreateWorkspace);
    };
  }, []);

  useEffect(() => {
    const handleCollapse = () => setIsExpanded(false);
    window.addEventListener('collapseSidebar', handleCollapse);
    return () => window.removeEventListener('collapseSidebar', handleCollapse);
  }, []);

  // if (userLoading) return null;//<div className='loading'>Loading...</div>;
  if (userError) return <div>Error: {userError}</div>;




  return (
    <>
      <aside className={`zoobbe-global-sidebar ${isExpanded ? 'expanded' : 'collapsed'}`}>
        {/* Logo Section */}
        <div className="sidebar-logo">
          <Link to={`/u/${user?.user?.username}/boards`} className="logo-link">
            {isWhiteLabel() ? (
              <BrandLogo
                size="medium"
                showText={isExpanded}
                useIcon={!isExpanded}
                className="sidebar-brand-logo"
              />
            ) : (
              <>

                {isExpanded ? (
                  <div className="logo-icon">
                    {currentTheme === 'dark' && <LogoMobile height='30px' width='auto' />}
                    {currentTheme === 'light' && <LogoMobile height='30px' width='auto' />}
                  </div>
                ) : (
                  <div className="logo-icon">
                    {currentTheme === 'dark' && <LogoMobile height='30px' width='auto' />}
                    {currentTheme === 'light' && <LogoMobile height='30px' width='auto' />}
                  </div>
                )}
              </>
            )}
          </Link>
          <button
            className="sidebar-toggle"
            onClick={handleToggleSidebar}
            data-tooltip-content={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
            data-tooltip-position="right"
          >
            {isExpanded ? (
              <Close size={20} color='var(--primary-text-color)' />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill={'var(--primary-text-color)'}><path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm120-80v-560H200v560h120Zm80 0h360v-560H400v560Zm-80 0H200h120Z" /></svg>
            )}
          </button>
        </div>


        {/* Main Navigation */}
        <nav className="sidebar-nav">
          <ul className="sidebar-items">
            {isBoardRoute ? (
              /* Board context: show workspace boards */
              <SidebarBoardList
                isExpanded={isExpanded}
                currentBoard={board}
                workspaceBoards={isBoardContext ? workspaceBoards : []}
                workspaceId={board?.wShortId}
              />
            ) : (
              <>
                {/* Actions Section */}
                {sidebarItems.home !== false && (
                  <li
                    className="sidebar-item action-item workspaces-page"
                    onClick={handleHomeClick}
                    data-tooltip-content={!isExpanded ? "Home" : ""}
                    data-tooltip-position="right"
                  >
                    <div className="item-icon">
                      <Home size={20} color='var(--brand-color)' />
                    </div>
                    {isExpanded && <span className="item-label">Home</span>}
                  </li>
                )}

                {sidebarItems.workspaces !== false && (
                  <li
                    className="sidebar-item action-item workspaces-page"
                    onClick={() => handleNavClick('navWorkspaces')}
                    data-tooltip-content={!isExpanded ? "Workspaces" : ""}
                    data-tooltip-position="right"
                  >
                    <div className="item-icon">
                      <Workspaces size={20} color='var(--brand-color)' />
                    </div>
                    {isExpanded && <span className="item-label">Workspaces</span>}
                  </li>
                )}

                {sidebarItems.whiteLabel !== false && (
                  <li
                    className="sidebar-item action-item white-label"
                    onClick={handleWhiteLabelClick}
                    data-tooltip-content={!isExpanded ? "White Label" : ""}
                    data-tooltip-position="right"
                  >
                    <div className="item-icon">
                      <NewLabel size={20} color='var(--brand-color)' />
                      <span className="indicator"></span>
                    </div>
                    {isExpanded && <span className="item-label">White Label</span>}
                  </li>
                )}

                {user?.user?.verified && sidebarItems.createWorkspace !== false && canCreateWorkspace && (
                  <li
                    className="sidebar-item action-item create-workspace"
                    onClick={handleCreateWorkspace}
                    data-tooltip-content={!isExpanded ? "Create Workspace" : ""}
                    data-tooltip-position="right"
                  >
                    <div className="item-icon">
                      <Add size={20} color='var(--primary-text-color)' />
                    </div>
                    {isExpanded && <span className="item-label">Create</span>}
                  </li>
                )}

                {/* Search Section */}
                {sidebarItems.search !== false && (
                  <li
                    className={`sidebar-item search-trigger ${isShowSearchResult ? 'active' : ''}`}
                    onClick={handleSearchClick}
                    data-tooltip-content={!isExpanded ? "Search" : ""}
                    data-tooltip-position="right"
                  >
                    <div className="item-icon">
                      <Search size={20} />
                    </div>
                    {isExpanded && <span className="item-label">Search</span>}
                  </li>
                )}

                {sidebarItems.recent !== false && (
                  <li
                    className={`sidebar-item nav-item ${activeNav === 'navRecent' ? 'active' : ''}`}
                    id='nav-recent'
                    onClick={() => handleNavClick('navRecent', { boardId: board?.shortId })}
                    ref={recentRef}
                    data-tooltip-content={!isExpanded ? "Recent" : ""}
                    data-tooltip-position="right"
                  >
                    <div className="item-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="var(--primary-text-color)">
                        <path d="M480-80q-155 0-269-103T82-440h81q15 121 105.5 200.5T480-160q134 0 227-93t93-227q0-134-93-227t-227-93q-86 0-159.5 42.5T204-640h116v80H88q29-140 139-230t253-90q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm112-232L440-464v-216h80v184l128 128-56 56Z" />
                      </svg>
                    </div>
                    {isExpanded && <span className="item-label">Recent</span>}
                  </li>
                )}

                {sidebarItems.starred !== false && (
                  <li
                    className={`sidebar-item nav-item ${activeNav === 'navStarred' ? 'active' : ''}`}
                    id='nav-starred'
                    onClick={() => handleNavClick('navStarred', { boardId: board?.shortId })}
                    ref={starredRef}
                    data-tooltip-content={!isExpanded ? "Starred" : ""}
                    data-tooltip-position="right"
                  >
                    <div className="item-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="var(--primary-text-color)">
                        <path d="m363-310 117-71 117 71-31-133 104-90-137-11-53-126-53 126-137 11 104 90-31 133ZM480-28 346-160H160v-186L28-480l132-134v-186h186l134-132 134 132h186v186l132 134-132 134v186H614L480-28Zm0-112 100-100h140v-140l100-100-100-100v-140H580L480-820 380-720H240v140L140-480l100 100v140h140l100 100Zm0-340Z" />
                      </svg>
                    </div>
                    {isExpanded && <span className="item-label">Starred</span>}
                  </li>
                )}

                {sidebarItems.pages !== false && (
                  <li
                    className={`sidebar-item nav-item ${activeNav === 'navPages' ? 'active' : ''}`}
                    id='nav-pages'
                    onClick={() => handleNavClick('navPages')}
                    data-tooltip-content={!isExpanded ? "Pages" : ""}
                    data-tooltip-position="right"
                  >
                    <div className="item-icon">
                      <TextSnippet size={20} />
                    </div>
                    {isExpanded && <span className="item-label">Pages</span>}
                  </li>
                )}
              </>
            )}



            {isBoardRoute && (
              <li
                className={`sidebar-item bottom-item ${activeNav === 'myDay' ? 'active' : ''}`}
                onClick={() => handleNavClick('myDay')}
                data-tooltip-content={!isExpanded ? "My Day" : ""}
                data-tooltip-position="right"
              >
                <div className="item-icon">
                  <MyDay size={20} color='var(--white)' />
                  {myDayIncompleteCount > 0 && <span className='myday-counter'>{myDayIncompleteCount}</span>}
                </div>
                {isExpanded && <span className="item-label">My Day</span>}
              </li>
            )}

            <li
              className={`sidebar-item bottom-item ${activeNav === 'chat' ? 'active' : ''} ${chatUnreadCount > 0 ? 'has-notification' : ''}`}
              onClick={() => handleNavClick('chat')}
              data-tooltip-content={!isExpanded ? "Messages" : ""}
              data-tooltip-position="right"
            >
              <div className="item-icon">
                <Messages size={20} color='var(--white)' />
                {chatUnreadCount > 0 && <span className='notification-counter'>{chatUnreadCount}</span>}
              </div>
              {isExpanded && <span className="item-label">Messages</span>}
            </li>

            {sidebarItems.notifications !== false && (
              <li
                className={`sidebar-item bottom-item ${activeNav === 'notifications' ? 'active' : ''} ${newNotification > 0 ? 'has-notification' : ''}`}
                ref={notificationsRef}
                id="notifications"
                onClick={() => handleShowNotifications('notifications')}
                data-tooltip-content={!isExpanded ? "Notifications" : ""}
                data-tooltip-position="right"
              >
                <div className="item-icon">
                  <NotificationsIcon size={18} />
                  {newNotification > 0 && <span className='notification-counter'>{newNotification}</span>}
                </div>
                {isExpanded && <span className="item-label">Notifications</span>}
              </li>
            )}

            {isBoardRoute && sidebarItems.search !== false && (
              <li
                className={`sidebar-item bottom-item ${isShowSearchResult ? 'active' : ''}`}
                onClick={handleSearchClick}
                data-tooltip-content={!isExpanded ? "Search" : ""}
                data-tooltip-position="right"
              >
                <div className="item-icon">
                  <Search size={20} />
                </div>
                {isExpanded && <span className="item-label">Search</span>}
              </li>
            )}

            {/* Bottom Section Items */}
            {sidebarItems.feedback !== false && (
              <li
                className={`sidebar-item bottom-item ${activeNav === 'feedBack' ? 'active' : ''}`}
                onClick={() => handleNavClick('feedBack')}
                data-tooltip-content={!isExpanded ? "Feedback" : ""}
                data-tooltip-position="right"
                id='feedback-button'
              >
                <div className="item-icon">
                  <Campaign size={18} />
                </div>
                {isExpanded && <span className="item-label">Feedback</span>}
              </li>
            )}

            {sidebarItems.profile !== false && (
              <li
                className={`sidebar-item bottom-item profile ${isShowProfile ? 'active' : ''}`}
                ref={profileRef}
                id="popover-profile"
                data-popover-trigger
                onClick={(e) => {
                  handlePopoverClick(e, 'profile', { userInfo: user, setIsShowProfile });
                }}
                data-tooltip-content={!isExpanded ? user?.user?.username : ""}
                data-tooltip-position="right"
              >
                <div className="profile-avatar">
                  {user?.user?.profilePicture ? (
                    <div className="image-placeholder-container">
                      <img
                        className="avatar-image"
                        src={user?.user?.profilePicture}
                        alt={user?.user?.username}
                        loading="lazy"
                        style={{
                          filter: user?.user?.userStatus === 'inactive' ? 'grayscale(100%)' : 'none'
                        }}
                      />
                      {user?.user?.userStatus === 'inactive' && (
                        <span className="inactive-indicator" title="Inactive user">
                          <span className="material-symbols-outlined">schedule</span>
                        </span>
                      )}
                      {user?.user?.userStatus !== 'inactive' && <MemberOnlineStatus member={user?.user} />}
                    </div>
                  ) : (
                    <ImagePlaceholder
                      member={user?.user}
                      key={user?.user?._id}
                      size={32}
                      text={user?.user?.username}
                      className={`${isShowProfile ? ' active' : ''}`}
                    />
                  )}
                  {hasNewRelease && <span className="new-release-dot" />}
                </div>
                {isExpanded && (
                  <div className="profile-info">
                    <span className="profile-name">{user?.user?.username}</span>
                    <span className="profile-email">{user?.user?.email}</span>
                  </div>
                )}
              </li>
            )}
          </ul>
        </nav>



      </aside>

      {/* Release tooltip rendered via portal to escape sidebar overflow */}
      {showReleaseTip && profileRef.current && ReactDOM.createPortal(
        <div
          className="release-tooltip-portal"
          style={{
            left: profileRef.current.getBoundingClientRect().right + 12,
            top: profileRef.current.getBoundingClientRect().top + (profileRef.current.getBoundingClientRect().height / 2) - 16,
          }}
        >
          <span>New updates available! <Link to="/whats-new" className="release-tooltip-link" onClick={dismissReleaseTip}>What's New</Link></span>
          <button className="release-tooltip-close" onClick={(e) => { e.stopPropagation(); dismissReleaseTip(); }}>&times;</button>
        </div>,
        document.body
      )}

      {/* Search Modal */}
      {isShowSearchResult && (
        <div className={`header-center ${isShowSearchResult ? 'active-search' : ''}`}>
          <div className={`zoobbe-header__search-container ${isShowSearchResult ? 'isOpen' : ''} ${isFocused ? 'focused' : ''}`} ref={searchRef}>
            <div className="search-content">
              <Search size={20} />
              <input
                className="zoobbe-header__search-input"
                type="text"
                placeholder="Search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => {
                  setIsFocused(true);
                  setSearchResult(true);
                }} // Add focused class and show SearchResults
                onBlur={() => setIsFocused(false)} // Remove focused class and hide SearchResults
                autoComplete="off"
              />
            </div>
            {isShowSearchResult && <SearchResults boardId={board?.shortId} query={query} setQuery={setQuery} setSearchResult={setSearchResult} />}
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
